import { PlaceholderKey } from './placeholder-keys';
import type { PlaceholderData, PlaceholderKind } from './types';

type RegistryEntry = {
  label: string;
  output: 'text' | 'image';
  missingFields: (data: PlaceholderData) => readonly string[];
};

const teamMissing = (d: PlaceholderData) => (d.teamNumber ? [] : ['teamNumber']);

const teamAndPlayerMissing = (d: PlaceholderData): readonly string[] => {
  const out: string[] = [];
  if (!d.teamNumber) out.push('teamNumber');
  if (!d.playerNumber) out.push('playerNumber');
  return out;
};

const none = () => [] as const;

export const PLACEHOLDER_KEY_REGISTRY = {
  [PlaceholderKey.TEAM_NAME]:         { label: 'Team name',         output: 'text',  missingFields: teamMissing },
  [PlaceholderKey.TEAM_SHORT_NAME]:   { label: 'Team short name',   output: 'text',  missingFields: teamMissing },
  [PlaceholderKey.TEAM_LOGO]:         { label: 'Team logo',         output: 'image', missingFields: teamMissing },
  [PlaceholderKey.SCORE]:             { label: 'Score',             output: 'text',  missingFields: none },
  [PlaceholderKey.CHAMPIONSHIP_NAME]: { label: 'Championship name', output: 'text',  missingFields: none },
  [PlaceholderKey.CHAMPIONSHIP_LOGO]: { label: 'Championship logo', output: 'image', missingFields: none },
  [PlaceholderKey.TOURNAMENT_NAME]:   { label: 'Tournament name',   output: 'text',  missingFields: none },
  [PlaceholderKey.MATCH_DATE]:        { label: 'Match date',        output: 'text',  missingFields: none },
  [PlaceholderKey.MATCH_TIME]:        { label: 'Match time',        output: 'text',  missingFields: none },
  [PlaceholderKey.MATCH_VENUE]:       { label: 'Match venue',       output: 'text',  missingFields: none },
  [PlaceholderKey.PLAYER_NAME]:       { label: 'Player name',       output: 'text',  missingFields: teamAndPlayerMissing },
  [PlaceholderKey.PLAYER_NUMBER]:     { label: 'Player number',     output: 'text',  missingFields: teamAndPlayerMissing },
  [PlaceholderKey.PLAYER_PHOTO]:      { label: 'Player photo',      output: 'image', missingFields: teamAndPlayerMissing },
} satisfies Record<PlaceholderKey, RegistryEntry>;

export function getRegistryEntry(key: string): RegistryEntry | undefined {
  return (PLACEHOLDER_KEY_REGISTRY as Record<string, RegistryEntry>)[key];
}

const expectedOutputForKind = (kind: PlaceholderKind | undefined): 'text' | 'image' | undefined => {
  if (kind === 'placeholder-image') return 'image';
  if (kind === 'placeholder-text') return 'text';
  return undefined;
};

export function isKeyCompatibleWithKind(key: string, kind: PlaceholderKind | undefined): boolean {
  const entry = getRegistryEntry(key);
  const expected = expectedOutputForKind(kind);
  if (!entry || !expected) return false;
  return entry.output === expected;
}

export function getKeysForKind(kind: PlaceholderKind): readonly PlaceholderKey[] {
  const expected = expectedOutputForKind(kind);
  if (!expected) return [];
  return (Object.values(PlaceholderKey) as PlaceholderKey[]).filter(
    (k) => PLACEHOLDER_KEY_REGISTRY[k].output === expected,
  );
}

export function missingFieldsFor(data: PlaceholderData): readonly string[] {
  const entry = getRegistryEntry(data.placeholderKey);
  if (!entry) return ['placeholderKey'];
  const expected = expectedOutputForKind(data.kind);
  if (expected && entry.output !== expected) return ['placeholderKey'];
  return entry.missingFields(data);
}
